import { useCallback, useMemo } from "react";
import { Column } from "../ColumnsSelector/ColumnsSelector";
import { ColumnIds } from "../../../features/pricing_list/hooks/columns/common";
import * as _ from "lodash";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";

export interface ShowHideProvisionColumnsButtonProps<T extends React.Key> {
	availableColumns: Column<T>[];
	selectedColumns: Column<T>[];
	onColumnToggled: (column: Column<T>) => void;
	onClick?: () => void;
}

const provisionColumns = [
	ColumnIds.SETUPFEE,
	ColumnIds.IMBALANCECOSTPROVISION,
	ColumnIds.PROFILECOSTPROVISION,
	ColumnIds.DELTAPROFILECOSTPROVISION,
	ColumnIds.SHAPEPROVISION,
	ColumnIds.SHAPEBIDASKPROVISION,
];

export function ShowHideProvisionColumnsButton(
	props: ShowHideProvisionColumnsButtonProps<ColumnIds>
) {
	const isOpen = useMemo(
		() =>
			props.selectedColumns.some((selectColumn) =>
				provisionColumns.includes(selectColumn.id)
			),
		[props.selectedColumns]
	);
	const onClick = useCallback(() => {
		if (isOpen) {
			const columnsToClose: Column<ColumnIds>[] = provisionColumns
				.map((columnId) =>
					_.find(
						props.availableColumns,
						(available) => available.id === columnId
					)
				)
				.filter((column) => !!column)
				.filter((column) =>
					props.selectedColumns
						.map((selectedColumn) => selectedColumn.id)
						.includes(column?.id as ColumnIds)
				) as Column<ColumnIds>[];
			columnsToClose.forEach(props.onColumnToggled);
		} else {
			const columnsToOpen: Column<ColumnIds>[] = provisionColumns
				.map((columnId) =>
					_.find(
						props.availableColumns,
						(available) => available.id === columnId
					)
				)
				.filter((column) => !!column)
				.filter(
					(column) =>
						!props.selectedColumns
							.map((selectedColumn) => selectedColumn.id)
							.includes(column?.id as ColumnIds)
				) as Column<ColumnIds>[];
			columnsToOpen.forEach(props.onColumnToggled);
		}
		if (props.onClick) {
			props.onClick();
		}
	}, [
		isOpen,
		props.selectedColumns,
		props.onColumnToggled,
		props.availableColumns,
		props.onClick,
	]);

	return (
		<MenuItem
			color="secondary"
			id="column-selector-button"
			onClick={onClick}
		>
			<ListItemIcon>
				{isOpen ? (
					<VisibilityOffIcon sx={{ color: "black" }} />
				) : (
					<VisibilityIcon sx={{ color: "black" }} />
				)}
			</ListItemIcon>
			<ListItemText>
				{(isOpen ? "Hide" : "Show") + " provisions"}
			</ListItemText>
		</MenuItem>
	);
}
